import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { SummaryItem } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'

import { useFormatNumber } from '@hooks/useFormatNumber'

interface BrokerSummaryFetchProps {
  search: SearchResponseProps
}

interface SearchResponseProps {
  search: FilterQueryProps
  debouncedSearchTerm: string | null
}

export const useBrokerSummaryFetch = (props: BrokerSummaryFetchProps) => {
  const { search } = props

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { t } = useTranslation()
  const { formatNumber, formatMoney } = useFormatNumber()

  const searchKey = useMemo(
    () => ({
      ...search.search,
      ca_search_TextSearch: search.debouncedSearchTerm,
      locale,
    }),
    [search.search, search.debouncedSearchTerm, locale]
  )

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.ib.incomeSummary(searchKey),
    queryFn: () =>
      apiClient.getIntroducingBrokerIncomeSummary({
        search: { ...search.search, ca_search_TextSearch: search.debouncedSearchTerm },
        pageIndex: 1,
        caller: 'ca',
        languageId: locale,
      }),
  })

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.Total Volume, Lots'),
        value: formatNumber(data?.volume ?? 0),
      },
      {
        title: t('IB.Total Commission'),
        value: formatMoney(data?.amount ?? 0, 'USD', (data?.amount || 0) > 0 ? 2 : 0),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, t])

  return { summaryItems, isLoading }
}
